<template>
  <div class="container mt-5">
    <h3>{{ category.name }}</h3>

    <span v-if="!this.dataReady">{{ $t('DiagnosticsCategoryComponent.noDataAvailable') }}</span>

    <v-chart v-if="radarOptions && this.dataReady && this.indicators.length > 2"
             class="chart"
             :option="radarOptions"
             style="height:300px;"/>

    <table class="table table-striped table-bordered table-hover table-responsive"
           v-if="this.dataReady">
      <thead>
      <tr>
        <th rowspan="3"
            colspan="2"
            scope="col">
          {{ $t('DiagnosticsCategoryComponent.indicator') }}
        </th>
        <th v-if="showAverage"
            rowspan="3">
          AVG
        </th>
      </tr>
      <tr>
        <th v-for="farm in this.farms"
            :colspan="Object.keys(results[farm.__id]).length"
            :key="farm.__id"
            scope="col">
          {{ farm.label }}
        </th>
      </tr>
      <tr>
        <template v-for="farm in this.farms">
          <th v-for="date in Object.keys(results[farm.__id])"
              scope="col">
            {{ date }}
          </th>
        </template>

      </tr>
      </thead>
      <tbody>
      <tr v-for="indicator in indicators"
          :key="indicator.id">
        <td class="align-middle"
            style="max-width:100px;">
          {{ indicator.name }}<br/>
          <span class="small"
                v-if="this.type === 'dimensions'">
            ({{ $t('weight') }}: {{ (indicator.weight_in_dimension / 100).toFixed(2) }})
          </span>
        </td>
        <td class="align-middle text-start"
            @click="indicator.collapsed = !indicator.collapsed">
          <div class="row">
            <div class="col-12">
              {{ indicator.description }}
              <i v-if="indicator.summary"
                 class="bi bi-info-circle"></i>
            </div>
          </div>
          <div v-if="!indicator.collapsed && indicator.summary"
               class="row m-2 p-2">
            <div class="col-3 col-sm-12 summary-wrapper"
                 style="white-space: break-spaces;">
              {{ indicator.summary }}
            </div>
          </div>
        </td>
        <td v-if="showAverage">
          0
        </td>

        <template v-for="farm in this.farms">
          <td class="align-middle"
              v-for="date in Object.keys(results[farm.__id])">

            <div class="row m-0 p-0">
              <div class="col-4 small justify-content-center">
                min = {{ indicator.minimum_value }}
              </div>
              <div class="col-4 small justify-content-center">
                <strong>val = {{
                    this.results[farm.__id][date]['values'][indicator.id]['indicator_value']
                  }}</strong>
              </div>
              <div class="col-4 small justify-content-center">
                max = {{ indicator.maximum_value }}
              </div>
            </div>

            <div class="row m-0 p-0">
              <div class="col-1 small p-0 m-0">
                0%
              </div>
              <div class="col-10 m-0 p-0">
                <div class="progress m-0 p-0"
                     style="width:100%; height: 1rem; background-color:#E7DBC0;">
                  <div class="progress-bar bg-success"
                       role="progressbar"
                       :style="{ width: `${(this.results[farm.__id][date]['values'][indicator.id]['indicator_value']/indicator.maximum_value)*100}%` }"
                       aria-valuenow="{{ (this.results[farm.__id][date]['values'][indicator.id]['indicator_value']/indicator.maximum_value) }}"
                       aria-valuemin="{{ indicator.minimum_value }}"
                       aria-valuemax="{{ indicator.maximum_value }}">
                    <strong>{{
                        ((this.results[farm.__id][date]['values'][indicator.id]['indicator_value'] / indicator.maximum_value) * 100).toFixed(0)
                      }}%</strong>
                  </div>
                </div>
              </div>
              <div class="col-1 small m-0 p-0">
                100%
              </div>
            </div>

          </td>
        </template>

      </tr>
      </tbody>
      <tfoot>
      <tr>
        <th colspan="2"
            scope="row">
          {{ (this.type === 'dimensions' ? $t('totalWeighted') : 'Total') }}
        </th>
        <th v-if="showAverage"></th>

        <template v-for="farm in this.farms">
          <th v-for="date in Object.keys(results[farm.__id])"
              scope="col">
            {{ this.totals[farm.__id][date] }} / 1
          </th>
        </template>
      </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import {getDiagnosticsResults, getDimensionIndicators, getPrincipleIndicators} from "@/utils";
import data from "bootstrap/js/src/dom/data";
import VChart from "@/main";

export default {
  components: {VChart},

  props: {
    category: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    farms: {
      type: Array,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    },
    showAverage: {
      type: Boolean,
      required: false
    },
    compareOverTime: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      dataReady: false,
      totals: {},
      results: {},
      indicators: [],
      radarOptions: null,
    }
  },
  computed: {
    data() {
      return data
    }
  },
  async mounted() {
    let response = [];
    if (this.type === 'dimensions') {
      response = await getDimensionIndicators(this.category.id);
    } else if (this.type === 'principles') {
      response = await getPrincipleIndicators(this.category.id);
    }

    if (response.status === 200) {
      this.indicators = response.data;
      await this.getResults();
    }
  },
  methods: {
    async getResults() {
      let farmIds = this.farms.map((farm) => farm.__id);
      let indicatorIds = this.indicators.map((indicator) => indicator.id);
      let response = await getDiagnosticsResults(farmIds, indicatorIds, this.showAverage, this.startDate, this.endDate);
      if (response.status === 200) {
        this.results = response.data;
        this.calculateTotals(this.type === 'dimensions');
        this.drawRadarChart();
        this.dataReady = true;
      }
    },

    calculateTotals(weighted) {
      this.farms.forEach(farm => {
        let total = [];
        Object.keys(this.results[farm.__id]).forEach(date => {
          total[date] = 0;
        });

        this.indicators.forEach(indicator => {
          Object.keys(this.results[farm.__id]).forEach(date => {
            if (weighted) {
              total[date] += Number(indicator.weight_in_dimension) * (Number(this.results[farm.__id][date]['values'][indicator.id]['indicator_value']) / Number(indicator.maximum_value));
            } else {
              total[date] += Number(this.results[farm.__id][date]['values'][indicator.id]['indicator_value']) / Number(indicator.maximum_value);
            }
          });
        });

        Object.keys(this.results[farm.__id]).forEach(date => {
          total[date] = (total[date] / 100).toFixed(3)
        });

        this.totals[farm.__id] = total
      });
    },

    drawRadarChart() {
      const chartIndicators = [];
      this.indicators.forEach(indicator => {
        chartIndicators.push({name: indicator.name, max: 1});
      });

      const chartData = [];
      const legendData = [];

      if (this.showAverage) {
        legendData.push("Average");
        let dataSeries = {name: '', value: []};
        dataSeries.name = "Average";
        this.indicators.forEach(indicator => {
          dataSeries.value.push(0.5);
        });
        chartData.push(dataSeries);
      }

      this.farms.forEach(farm => {
        Object.keys(this.results[farm.__id]).forEach(date => {
          legendData.push(farm.label + " (" + date + ")");

          let dataSeries = {name: '', value: []};
          dataSeries.name = farm.label + " (" + date + ")";
          this.indicators.forEach(indicator => {
            dataSeries.value.push(this.results[farm.__id][date]['values'][indicator.id]['indicator_value'] / indicator.maximum_value);
          })

          chartData.push(dataSeries);
        });
      });

      this.radarOptions = {
        legend: {
          orient: 'vertical',
          top: 'top',
          left: 'left',
          data: legendData,
        },
        radar: {
          indicator: chartIndicators,
        },
        series: [
          {
            type: 'radar',
            data: chartData,
          },
        ],
      };
    },
  },
  watch: {
    async farms(newVal, oldVal) {
      this.dataReady = false;
      await this.getResults();
    },
    compareOverTime(newVal, oldVal) {
      // this.sumDateCols();
    }
  },
};
</script>